import React, { useState, memo, useCallback } from 'react';
import { Search, ChevronRight, Cpu, X, PanelLeftClose, PanelLeft, GitCompare } from 'lucide-react';
import { AlgorithmMeta, ALGORITHM_CATEGORIES } from '../../../core/registry/algorithmRegistry';

interface AlgorithmSidebarProps {
  selectedAlgo: AlgorithmMeta | null;
  onSelectAlgorithm: (algo: AlgorithmMeta) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Mobile: drawer is open (overlay mode on narrow screens) */
  isMobileOpen?: boolean;
  /** Mobile: close the drawer */
  onMobileClose?: () => void;
  /** Compare mode: sidebar routes clicks to compare slots */
  compareMode?: boolean;
  /** Algo currently in compare slot A */
  compareSlotA?: AlgorithmMeta | null;
  /** Algo currently in compare slot B */
  compareSlotB?: AlgorithmMeta | null;
  /** Called when user picks an algo in compare mode */
  onSelectForCompare?: (algo: AlgorithmMeta) => void;
  /** Toggle compare mode on/off */
  onToggleCompareMode?: () => void;
}

const CATEGORY_ICONS: Record<string, string> = {
  Sorting:           '⇅',
  Graph:             '◈',
  'Data Structures': '◻',
  DP:                '◫',
  Search:            '⊙',
  String:            '≋',
  Math:              'π',
  Greedy:            '◆',
  Backtracking:      '↩',
  Geometry:          '△',
  SAT:               '⊕',
};

function getCategoryIcon(name: string): string {
  return CATEGORY_ICONS[name] ?? '◉';
}

const totalAlgorithmCount = ALGORITHM_CATEGORIES.reduce(
  (acc: number, cat) => acc + cat.algorithms.length,
  0,
);

export const AlgorithmSidebar: React.FC<AlgorithmSidebarProps> = memo(({
  selectedAlgo,
  onSelectAlgorithm,
  isCollapsed = false,
  onToggleCollapse,
  isMobileOpen = false,
  onMobileClose,
  compareMode = false,
  compareSlotA = null,
  compareSlotB = null,
  onSelectForCompare,
  onToggleCompareMode,
}) => {
  const [searchQuery, setSearchQuery]           = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = useCallback((categoryName: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryName)) next.delete(categoryName);
      else next.add(categoryName);
      return next;
    });
  }, []);

  const clearSearch = useCallback(() => setSearchQuery(''), []);

  const filteredCategories = ALGORITHM_CATEGORIES.map(category => ({
    ...category,
    algorithms: category.algorithms.filter(algo =>
      algo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      algo.id.toLowerCase().includes(searchQuery.toLowerCase()),
    ),
  })).filter(category => category.algorithms.length > 0);

  const isSearching = searchQuery.length > 0;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-30 sm:hidden"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className="h-full flex flex-col flex-shrink-0 overflow-hidden"
        style={{
          /* Desktop: inline column; mobile: fixed overlay drawer */
          width: isCollapsed ? 'var(--sidebar-left-collapsed)' : 'var(--sidebar-left-expanded)',
          background: 'linear-gradient(180deg, #111113 0%, #0c0c0e 100%)',
          boxShadow: '1px 0 0 0 rgba(255,255,255,0.06), 6px 0 32px rgba(0,0,0,0.5)',
          transition: 'width 0.28s cubic-bezier(0.16,1,0.3,1), transform 0.28s cubic-bezier(0.16,1,0.3,1)',
        }}
        /* On mobile (<640 px) use a fixed overlay drawer */
        data-mobile-open={isMobileOpen}
      >
      {/* ── Header ── */}
      <div
        className="flex items-center gap-2.5 px-3 py-3.5 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        {/* Logo */}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
            boxShadow: '0 0 14px rgba(99,102,241,0.35)',
          }}
        >
          <Cpu className="w-4 h-4 text-white" />
        </div>

        {!isCollapsed && (
          <div className="flex-1 min-w-0 animate-fade-in">
            <h1 className="text-[13px] font-semibold tracking-tight text-zinc-100 leading-none">
              Algo Visualiser
            </h1>
            <p className="text-[10px] text-zinc-600 mt-0.5 font-mono tracking-widest uppercase">
              {totalAlgorithmCount} algorithms
            </p>
          </div>
        )}

        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200"
            style={{ color: '#52525b' }}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.color = '#d4d4d8';
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.color = '#52525b';
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            {isCollapsed
              ? <PanelLeft className="w-3.5 h-3.5" />
              : <PanelLeftClose className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {/* ── Search ── */}
      {!isCollapsed && (
        <div className="px-3 pt-3 pb-2 flex-shrink-0 animate-fade-in">
          <div
            className="relative flex items-center rounded-lg overflow-hidden transition-all duration-200"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
            onFocusCapture={e => {
              (e.currentTarget as HTMLDivElement).style.border = '1px solid rgba(59,130,246,0.35)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = '0 0 0 3px rgba(59,130,246,0.08)';
            }}
            onBlurCapture={e => {
              (e.currentTarget as HTMLDivElement).style.border = '1px solid rgba(255,255,255,0.07)';
              (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
            }}
          >
            <Search className="absolute left-2.5 w-3.5 h-3.5 text-zinc-600 flex-shrink-0 pointer-events-none" />
            <input
              type="text"
              placeholder="Search algorithms…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-transparent text-zinc-200 placeholder-zinc-700 pl-8 pr-8 py-2 text-xs font-medium focus:outline-none"
            />
            {isSearching && (
              <button
                onClick={clearSearch}
                className="absolute right-2 text-zinc-600 hover:text-zinc-300 transition-colors duration-150"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Category / Algorithm List ── */}
      <div className="flex-1 overflow-y-auto px-2 pb-3">
        {isCollapsed ? (
          /* Collapsed — icon-only category dots */
          <div className="flex flex-col items-center gap-1 pt-2">
            {ALGORITHM_CATEGORIES.map(category => {
              const hasSelected = category.algorithms.some(a => a.id === selectedAlgo?.id);
              return (
                <div
                  key={category.name}
                  title={category.name}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-mono transition-all duration-200 cursor-default"
                  style={{
                    color: hasSelected ? '#60a5fa' : '#52525b',
                    background: hasSelected ? 'rgba(59,130,246,0.12)' : 'transparent',
                  }}
                >
                  {getCategoryIcon(category.name)}
                </div>
              );
            })}
          </div>
        ) : (
          /* Expanded — accordion */
          <div className="space-y-0.5 pt-1">
            {filteredCategories.map(category => {
              const isExpanded  = expandedCategories.has(category.name) || isSearching;
              const hasSelected = category.algorithms.some(a => a.id === selectedAlgo?.id);

              return (
                <div key={category.name} className="animate-fade-in">
                  {/* Category row */}
                  <button
                    onClick={() => !isSearching && toggleCategory(category.name)}
                    className="w-full px-2.5 py-2 flex items-center gap-2 rounded-lg transition-all duration-200"
                    style={{ color: hasSelected ? '#e4e4e7' : '#71717a' }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <span
                      className="w-5 h-5 rounded flex items-center justify-center text-[11px] font-mono flex-shrink-0 transition-all duration-200"
                      style={{
                        color:      hasSelected ? '#60a5fa' : 'rgba(255,255,255,0.28)',
                        background: hasSelected ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
                      }}
                    >
                      {getCategoryIcon(category.name)}
                    </span>
                    <span className="flex-1 text-left text-xs font-medium tracking-tight">
                      {category.name}
                    </span>
                    <span className="text-[10px] font-mono tabular-nums" style={{ color: '#3f3f46' }}>
                      {category.algorithms.length}
                    </span>
                    {!isSearching && (
                      <ChevronRight
                        className="w-3 h-3 flex-shrink-0 transition-transform duration-200"
                        style={{
                          color: '#3f3f46',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                    )}
                  </button>

                  {/* Algorithm items */}
                  {isExpanded && (
                    <div
                      className="ml-3 pl-2.5 space-y-0.5 mt-0.5 mb-1"
                      style={{ borderLeft: '1px solid rgba(255,255,255,0.05)' }}
                    >
                      {category.algorithms.map(algorithm => {
                        const isActive  = !compareMode && selectedAlgo?.id === algorithm.id;
                        const inSlotA   = compareSlotA?.id === algorithm.id;
                        const inSlotB   = compareSlotB?.id === algorithm.id;
                        const inCompare = inSlotA || inSlotB;

                        return (
                          <button
                            key={algorithm.id}
                            onClick={() => compareMode
                              ? onSelectForCompare?.(algorithm)
                              : onSelectAlgorithm(algorithm)
                            }
                            className="w-full px-2.5 py-1.5 rounded-lg text-left flex items-center gap-1.5 transition-all duration-200"
                            style={{
                              background: isActive
                                ? 'rgba(59,130,246,0.10)'
                                : inCompare
                                ? 'rgba(99,102,241,0.07)'
                                : 'transparent',
                              border: isActive
                                ? '1px solid rgba(59,130,246,0.22)'
                                : inCompare
                                ? '1px solid rgba(99,102,241,0.18)'
                                : '1px solid transparent',
                            }}
                            onMouseEnter={e => {
                              if (!isActive && !inCompare)
                                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                            }}
                            onMouseLeave={e => {
                              if (!isActive && !inCompare)
                                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                            }}
                          >
                            <p
                              className="flex-1 font-medium tracking-tight transition-colors duration-150"
                              style={{
                                color: isActive ? '#93c5fd' : inCompare ? '#c4b5fd' : '#a1a1aa',
                                fontSize: '13px',
                                whiteSpace: 'normal',
                                lineHeight: '1.3',
                              }}
                              title={algorithm.name}
                            >
                              {algorithm.name}
                            </p>
                            {/* Compare slot badges */}
                            {inSlotA && (
                              <span className="text-[8px] font-bold px-1 py-0.5 rounded flex-shrink-0"
                                style={{ background: 'rgba(59,130,246,0.2)', color: '#93c5fd' }}>A</span>
                            )}
                            {inSlotB && (
                              <span className="text-[8px] font-bold px-1 py-0.5 rounded flex-shrink-0"
                                style={{ background: 'rgba(167,139,250,0.2)', color: '#c4b5fd' }}>B</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      {!isCollapsed && (
        <div
          className="px-3 py-2 flex-shrink-0 flex items-center gap-2"
          style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
        >
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-55" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
          </span>
          <span className="flex-1 text-[10px] text-zinc-700 font-mono tracking-widest uppercase">
            Engine ready
          </span>
          {onToggleCompareMode && (
            <button
              onClick={onToggleCompareMode}
              title={compareMode ? 'Exit compare mode' : 'Compare two algorithms'}
              aria-pressed={compareMode}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold transition-colors duration-150 flex-shrink-0"
              style={{
                background: compareMode ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.04)',
                border: compareMode ? '1px solid rgba(99,102,241,0.38)' : '1px solid rgba(255,255,255,0.08)',
                color: compareMode ? '#c7d2fe' : '#52525b',
              }}
            >
              <GitCompare className="w-3 h-3" />
              Compare
            </button>
          )}
        </div>
      )}
      </aside>
    </>
  );
});
